import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cashflowAPI, jobsAPI, jobSubcontractorsAPI, requisitionsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format, addMonths, startOfMonth } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import {
  TrendingDown,
  Coins,
  Users,
  Truck,
  Package,
  BarChart3,
  Calendar,
  Filter,
  Eye,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  Save,
  Plus,
  Trash2,
} from 'lucide-react';

const Cashflow = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get('jobId');
  
  const [activeTab, setActiveTab] = useState(jobIdParam ? 'job' : 'summary');
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(jobIdParam || '');
  const [jobCashflow, setJobCashflow] = useState(null);
  const [summary, setSummary] = useState(null);
  const [projection, setProjection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectionFilters, setProjectionFilters] = useState({
    startMonth: format(startOfMonth(new Date()), 'yyyy-MM'),
    endMonth: format(addMonths(startOfMonth(new Date()), 5), 'yyyy-MM'),
    jobIds: 'all',
  });
  const [expandedJobs, setExpandedJobs] = useState({});
  
  // Overview tab filters
  const [overviewJobFilter, setOverviewJobFilter] = useState('');
  const [overviewPeriodStart, setOverviewPeriodStart] = useState('');
  const [overviewPeriodEnd, setOverviewPeriodEnd] = useState('');
  
  // Spread Payment Modal State
  const [showSpreadModal, setShowSpreadModal] = useState(false);
  const [spreadModalData, setSpreadModalData] = useState(null);
  const [spreadAllocations, setSpreadAllocations] = useState([]);

  const canView = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadJobs();
    if (activeTab === 'summary') {
      loadSummary();
    }
  }, []);

  useEffect(() => {
    if (selectedJobId && activeTab === 'job') {
      loadJobCashflow();
    }
  }, [selectedJobId, activeTab]);

  useEffect(() => {
    if (activeTab === 'projection') {
      loadProjection();
    }
  }, [activeTab, projectionFilters]);

  const loadJobs = async () => {
    try {
      const res = await jobsAPI.getAll();
      setJobs(res.data);
    } catch (error) {
      console.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await cashflowAPI.getSummary();
      setSummary(res.data);
    } catch (error) {
      toast.error('Failed to load cashflow summary');
    }
  };

  const loadJobCashflow = async () => {
    try {
      const res = await cashflowAPI.getJobCashflow(selectedJobId);
      setJobCashflow(res.data);
    } catch (error) {
      toast.error('Failed to load job cashflow');
    }
  };

  const loadProjection = async () => {
    try {
      const res = await cashflowAPI.getProjection(
        projectionFilters.startMonth,
        projectionFilters.endMonth,
        projectionFilters.jobIds
      );
      setProjection(res.data);
    } catch (error) {
      toast.error('Failed to load cashflow projection');
    }
  };

  const toggleJobExpand = (jobId) => {
    setExpandedJobs(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  // Filter summary jobs based on overview filters
  const filteredSummaryJobs = useMemo(() => {
    if (!summary?.jobs) return [];
    
    return summary.jobs.filter(job => {
      if (overviewJobFilter && job.job_id !== overviewJobFilter) return false;
      // Period filtering would require additional backend support
      // For now, we filter client-side by job only
      return true;
    });
  }, [summary, overviewJobFilter]);

  const filteredSummaryTotals = useMemo(() => {
    if (!filteredSummaryJobs.length) return summary?.summary || {};
    
    const totals = filteredSummaryJobs.reduce((acc, job) => ({
      total_planned_resource_cost: acc.total_planned_resource_cost + job.planned_resource_cost,
      total_actual_resource_cost: acc.total_actual_resource_cost + job.actual_resource_cost,
      total_subcontractor_cost: acc.total_subcontractor_cost + job.subcontractor_cost,
      total_material_cost: acc.total_material_cost + job.material_cost,
    }), {
      total_planned_resource_cost: 0,
      total_actual_resource_cost: 0,
      total_subcontractor_cost: 0,
      total_material_cost: 0,
    });
    
    return {
      ...totals,
      grand_total_planned: totals.total_planned_resource_cost + totals.total_subcontractor_cost + totals.total_material_cost,
      grand_total_actual: totals.total_actual_resource_cost + totals.total_subcontractor_cost + totals.total_material_cost,
    };
  }, [filteredSummaryJobs, summary]);

  const hasOverviewFilters = overviewJobFilter || overviewPeriodStart || overviewPeriodEnd;

  const clearOverviewFilters = () => {
    setOverviewJobFilter('');
    setOverviewPeriodStart('');
    setOverviewPeriodEnd('');
  };

  // Spread Payment Modal Functions
  const handleOpenSpreadModal = (item, type) => {
    setSpreadModalData({ ...item, type, jobId: selectedJobId });
    // Initialize allocations from existing data or start fresh
    if (item.monthly_allocations?.length > 0) {
      setSpreadAllocations([...item.monthly_allocations]);
    } else {
      // Start with current month
      setSpreadAllocations([{ month: format(new Date(), 'yyyy-MM'), amount: 0 }]);
    }
    setShowSpreadModal(true);
  };

  const handleAddAllocation = () => {
    const lastMonth = spreadAllocations.length > 0 
      ? spreadAllocations[spreadAllocations.length - 1].month 
      : format(new Date(), 'yyyy-MM');
    const nextMonth = format(addMonths(new Date(lastMonth + '-01'), 1), 'yyyy-MM');
    setSpreadAllocations([...spreadAllocations, { month: nextMonth, amount: 0 }]);
  };

  const handleRemoveAllocation = (index) => {
    setSpreadAllocations(spreadAllocations.filter((_, i) => i !== index));
  };

  const handleAllocationChange = (index, field, value) => {
    const updated = [...spreadAllocations];
    updated[index] = { ...updated[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setSpreadAllocations(updated);
  };

  const handleSaveAllocations = async () => {
    if (!spreadModalData) return;
    
    const totalAllocated = spreadAllocations.reduce((sum, a) => sum + a.amount, 0);
    const contractValue = spreadModalData.contract_value || 0;
    
    if (totalAllocated !== contractValue && contractValue > 0) {
      const proceed = window.confirm(
        `Total allocated (${formatCurrency(totalAllocated)}) does not match contract value (${formatCurrency(contractValue)}). Save anyway?`
      );
      if (!proceed) return;
    }
    
    try {
      if (spreadModalData.type === 'subcontractor') {
        await jobSubcontractorsAPI.update(spreadModalData.jobId, spreadModalData.id, {
          monthly_allocations: spreadAllocations
        });
      }
      toast.success('Payment schedule saved');
      setShowSpreadModal(false);
      loadJobCashflow(); // Refresh the data
    } catch (error) {
      toast.error('Failed to save payment schedule');
    }
  };

  const handleSpreadEvenly = () => {
    if (!spreadModalData || spreadAllocations.length === 0) return;
    const contractValue = spreadModalData.contract_value || 0;
    const perMonth = contractValue / spreadAllocations.length;
    const updated = spreadAllocations.map(a => ({ ...a, amount: Math.round(perMonth * 100) / 100 }));
    // Adjust last allocation for rounding differences
    const totalAllocated = updated.reduce((sum, a) => sum + a.amount, 0);
    if (updated.length > 0) {
      updated[updated.length - 1].amount += contractValue - totalAllocated;
      updated[updated.length - 1].amount = Math.round(updated[updated.length - 1].amount * 100) / 100;
    }
    setSpreadAllocations(updated);
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600">You do not have permission to view cashflow information.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Cashflow Management
          </h1>
          <p className="text-slate-600">Track and project job costs and expenses</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          {[
            { id: 'summary', label: 'Overview', icon: BarChart3 },
            { id: 'job', label: 'Job Breakdown', icon: Eye },
            { id: 'projection', label: 'Monthly Projection', icon: Calendar },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && summary && (
        <div className="space-y-6">
          {/* Filters Section */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {hasOverviewFilters && (
                <button
                  onClick={clearOverviewFilters}
                  className="ml-auto text-xs text-blue-600 hover:text-blue-700"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Job</label>
                <JobSearchSelect
                  jobs={jobs}
                  selectedJobId={overviewJobFilter}
                  onSelect={setOverviewJobFilter}
                  placeholder="All jobs..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Period Start</label>
                <input
                  type="month"
                  value={overviewPeriodStart}
                  onChange={(e) => setOverviewPeriodStart(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Period End</label>
                <input
                  type="month"
                  value={overviewPeriodEnd}
                  onChange={(e) => setOverviewPeriodEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Resource Costs (Planned)</span>
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(filteredSummaryTotals.total_planned_resource_cost)}</p>
              <p className="text-sm text-slate-500 mt-1">Actual: {formatCurrency(filteredSummaryTotals.total_actual_resource_cost)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Sub-contractor Costs</span>
                <Truck className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(filteredSummaryTotals.total_subcontractor_cost)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Material Costs</span>
                <Package className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(filteredSummaryTotals.total_material_cost)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <span className="text-blue-700">Grand Total (Planned)</span>
                <Coins className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-900">{formatCurrency(filteredSummaryTotals.grand_total_planned)}</p>
              <p className="text-sm text-blue-600 mt-1">Actual: {formatCurrency(filteredSummaryTotals.grand_total_actual)}</p>
            </div>
          </div>

          {/* Jobs Breakdown Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Cost by Job {hasOverviewFilters && <span className="text-sm font-normal text-slate-500">({filteredSummaryJobs.length} jobs)</span>}
              </h2>
            </div>
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Job</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Resources (Planned)</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Resources (Actual)</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Sub-contractors</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Materials</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredSummaryJobs.map((job) => (
                  <tr key={job.job_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      {job.job_code && <span className="text-xs font-mono text-blue-600 mr-2">{job.job_code}</span>}
                      <span className="font-medium text-slate-900">{job.job_title}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(job.planned_resource_cost)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(job.actual_resource_cost)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(job.subcontractor_cost)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(job.material_cost)}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCurrency(job.total_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Job Breakdown Tab */}
      {activeTab === 'job' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">Select Job</label>
            <JobSearchSelect
              jobs={jobs}
              selectedJobId={selectedJobId}
              onSelect={setSelectedJobId}
              placeholder="Search for a job by code, title, or location..."
              className="max-w-md"
            />
          </div>

          {jobCashflow && (
            <>
              {/* Job Header */}
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      {jobCashflow.job_code && <span className="text-blue-600 mr-2">{jobCashflow.job_code}</span>}
                      {jobCashflow.job_title}
                    </h2>
                    {jobCashflow.start_date && (
                      <p className="text-sm text-slate-600 mt-1">
                        {format(new Date(jobCashflow.start_date), 'MMM dd, yyyy')} - 
                        {jobCashflow.end_date ? format(new Date(jobCashflow.end_date), 'MMM dd, yyyy') : 'Ongoing'}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-500">Total Planned Cost</p>
                    <p className="text-2xl font-bold text-blue-600">{formatCurrency(jobCashflow.cost_breakdown.planned.total)}</p>
                  </div>
                </div>
              </div>

              {/* Cost Breakdown Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Resource Costs</p>
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(jobCashflow.cost_breakdown.planned.resource_cost)}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600">
                    Actual: {formatCurrency(jobCashflow.cost_breakdown.actual.resource_cost)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {jobCashflow.resource_assignments?.length || 0} team member assignments
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Truck className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Sub-contractor Costs</p>
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(jobCashflow.cost_breakdown.planned.subcontractor_cost)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {jobCashflow.subcontractors?.length || 0} sub-contractors assigned
                  </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Package className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Material Costs</p>
                      <p className="text-lg font-bold text-slate-900">{formatCurrency(jobCashflow.cost_breakdown.planned.material_cost)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {jobCashflow.requisitions?.length || 0} requisitions
                  </p>
                </div>
              </div>

              {/* Detailed Breakdowns */}
              {jobCashflow.resource_assignments?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleJobExpand('resources')}
                    className="w-full p-4 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50"
                  >
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Resource Assignments
                    </h3>
                    {expandedJobs['resources'] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedJobs['resources'] && (
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-4 py-2 text-sm font-medium text-slate-700">Team Member</th>
                          <th className="text-left px-4 py-2 text-sm font-medium text-slate-700">Period</th>
                          <th className="text-right px-4 py-2 text-sm font-medium text-slate-700">Planned Hours</th>
                          <th className="text-right px-4 py-2 text-sm font-medium text-slate-700">Rate</th>
                          <th className="text-right px-4 py-2 text-sm font-medium text-slate-700">Cost</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {jobCashflow.resource_assignments.map((ra, i) => (
                          <tr key={i}>
                            <td className="px-4 py-2 font-medium text-slate-900">{ra.member_name}</td>
                            <td className="px-4 py-2 text-slate-600 text-sm">
                              {format(new Date(ra.assignment_start), 'MMM dd')} - {format(new Date(ra.assignment_end), 'MMM dd')}
                            </td>
                            <td className="px-4 py-2 text-right text-slate-600">{ra.total_planned_hours} hrs</td>
                            <td className="px-4 py-2 text-right text-slate-600">{formatCurrency(ra.hourly_rate)}/hr</td>
                            <td className="px-4 py-2 text-right font-medium text-slate-900">{formatCurrency(ra.total_planned_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {jobCashflow.subcontractors?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggleJobExpand('subcontractors')}
                    className="w-full p-4 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50"
                  >
                    <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Sub-contractors
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                        Click to spread payments
                      </span>
                    </h3>
                    {expandedJobs['subcontractors'] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  {expandedJobs['subcontractors'] && (
                    <div className="p-4 space-y-4">
                      {jobCashflow.subcontractors.map((sc, i) => (
                        <div key={i} className="border border-slate-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="font-medium text-slate-900">{sc.subcontractor_name}</p>
                              <p className="text-sm text-slate-600">{sc.contractor_type}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-500">Contract Value</p>
                              <p className="text-lg font-bold text-slate-900">{formatCurrency(sc.contract_value || 0)}</p>
                            </div>
                          </div>
                          
                          {/* Monthly Payment Allocations */}
                          <div className="border-t border-slate-200 pt-4">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-medium text-slate-700">Monthly Payment Schedule</p>
                              <button
                                onClick={() => handleOpenSpreadModal(sc, 'subcontractor')}
                                className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg transition-colors"
                              >
                                Edit Schedule
                              </button>
                            </div>
                            {sc.monthly_allocations?.length > 0 ? (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {sc.monthly_allocations.map((alloc, ai) => (
                                  <div key={ai} className="bg-slate-50 p-2 rounded text-sm">
                                    <p className="text-slate-500">{alloc.month}</p>
                                    <p className="font-medium text-slate-900">{formatCurrency(alloc.amount)}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                                No payment schedule set. Click &quot;Edit Schedule&quot; to spread payments across months.
                              </p>
                            )}
                            {sc.monthly_allocations?.length > 0 && (
                              <div className="mt-2 flex items-center justify-between text-sm">
                                <span className="text-slate-500">Total Allocated:</span>
                                <span className={`font-medium ${
                                  sc.monthly_allocations.reduce((sum, a) => sum + a.amount, 0) === sc.contract_value
                                    ? 'text-green-600' : 'text-orange-600'
                                }`}>
                                  {formatCurrency(sc.monthly_allocations.reduce((sum, a) => sum + a.amount, 0))}
                                  {sc.monthly_allocations.reduce((sum, a) => sum + a.amount, 0) !== sc.contract_value && (
                                    <span className="ml-2 text-xs">
                                      (Outstanding: {formatCurrency(sc.contract_value - sc.monthly_allocations.reduce((sum, a) => sum + a.amount, 0))})
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Projection Tab */}
      {activeTab === 'projection' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-slate-500" />
              <h3 className="font-medium text-slate-900">Projection Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Start Month</label>
                <input
                  type="month"
                  value={projectionFilters.startMonth}
                  onChange={(e) => setProjectionFilters({ ...projectionFilters, startMonth: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">End Month</label>
                <input
                  type="month"
                  value={projectionFilters.endMonth}
                  onChange={(e) => setProjectionFilters({ ...projectionFilters, endMonth: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Jobs</label>
                <select
                  value={projectionFilters.jobIds}
                  onChange={(e) => setProjectionFilters({ ...projectionFilters, jobIds: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="all">All Active Jobs</option>
                  {jobs.filter(j => j.status !== 'completed').map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_code && `${job.job_code} - `}{job.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {projection && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <p className="text-sm text-blue-700">Total Planned Expenses</p>
                  <p className="text-3xl font-bold text-blue-900">{formatCurrency(projection.totals.planned)}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <p className="text-sm text-green-700">Total Actual Expenses</p>
                  <p className="text-3xl font-bold text-green-900">{formatCurrency(projection.totals.actual)}</p>
                </div>
              </div>

              {/* Monthly Breakdown */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-200">
                  <h3 className="font-semibold text-slate-900">Monthly Cashflow Projection</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-700">Month</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Resources</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Sub-contractors</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Materials</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Total Planned</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-slate-700">Total Actual</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {projection.projection.map((month) => (
                        <tr key={month.month} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {format(new Date(month.month + '-01'), 'MMMM yyyy')}
                          </td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(month.planned.resource_cost)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(month.planned.subcontractor_cost)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{formatCurrency(month.planned.material_cost)}</td>
                          <td className="px-4 py-3 text-right font-bold text-blue-600">{formatCurrency(month.planned.total)}</td>
                          <td className="px-4 py-3 text-right font-bold text-green-600">{formatCurrency(month.actual.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                      <tr>
                        <td className="px-4 py-3 font-bold text-slate-900">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                          {formatCurrency(projection.projection.reduce((sum, m) => sum + m.planned.resource_cost, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                          {formatCurrency(projection.projection.reduce((sum, m) => sum + m.planned.subcontractor_cost, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                          {formatCurrency(projection.projection.reduce((sum, m) => sum + m.planned.material_cost, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-blue-700">{formatCurrency(projection.totals.planned)}</td>
                        <td className="px-4 py-3 text-right font-bold text-green-700">{formatCurrency(projection.totals.actual)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Spread Payment Modal */}
      {showSpreadModal && spreadModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Spread Payment Schedule
                </h2>
                <p className="text-sm text-slate-500">
                  {spreadModalData.subcontractor_name || spreadModalData.name}
                </p>
              </div>
              <button onClick={() => setShowSpreadModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Contract Summary */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Contract Value:</span>
                  <span className="text-xl font-bold text-slate-900">{formatCurrency(spreadModalData.contract_value || 0)}</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-600">Total Allocated:</span>
                  <span className={`text-lg font-semibold ${
                    spreadAllocations.reduce((sum, a) => sum + a.amount, 0) === spreadModalData.contract_value
                      ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {formatCurrency(spreadAllocations.reduce((sum, a) => sum + a.amount, 0))}
                  </span>
                </div>
                {spreadAllocations.reduce((sum, a) => sum + a.amount, 0) !== spreadModalData.contract_value && (
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-slate-600">Outstanding:</span>
                    <span className="text-orange-600 font-medium">
                      {formatCurrency((spreadModalData.contract_value || 0) - spreadAllocations.reduce((sum, a) => sum + a.amount, 0))}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Quick Actions */}
              <div className="flex gap-2">
                <button
                  onClick={handleSpreadEvenly}
                  className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-sm transition-colors"
                >
                  Spread Evenly
                </button>
                <button
                  onClick={handleAddAllocation}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm transition-colors flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add Month
                </button>
              </div>
              
              {/* Allocations List */}
              <div className="space-y-3">
                <h3 className="font-medium text-slate-900">Monthly Allocations</h3>
                {spreadAllocations.map((allocation, index) => (
                  <div key={index} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                    <input
                      type="month"
                      value={allocation.month}
                      onChange={(e) => handleAllocationChange(index, 'month', e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={allocation.amount}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          handleAllocationChange(index, 'amount', value);
                        }}
                        placeholder="Amount"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                      />
                    </div>
                    <button
                      onClick={() => handleRemoveAllocation(index)}
                      className="p-2 hover:bg-red-100 rounded-lg text-red-500"
                      disabled={spreadAllocations.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              
              {/* Validation Message */}
              {spreadAllocations.reduce((sum, a) => sum + a.amount, 0) !== spreadModalData.contract_value && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-700">
                    <strong>Note:</strong> Total allocated amount does not match contract value. 
                    The difference will be tracked as outstanding value in following months.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowSpreadModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAllocations}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Schedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cashflow;
